import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NavigationHistoryService {
  private static readonly CACHE_KEYS = {
    PREVIOUS_ROUTE: 'nav_history_previous_route',
    CURRENT_ROUTE: 'nav_history_current_route',
  };

  // Entries that reach control tower with no contract screen in between, so back goes to the
  // contract. task-feed-v2 invitations are excluded — their back belongs on the invitations list.
  private static readonly CONTRACT_RETURN_ENTRY_ROUTES = ['/dashboard-v2', '/task-feed-v2/opportunities'];

  private previousRoute: string | null = null;
  private currentRoute: string | null = null;

  constructor(private router: Router) {
    this.restoreCachedRoutes();
    this.initializeTracking();
  }

  private initializeTracking(): void {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const newRoute = event.urlAfterRedirects;

      // Only update previous route if we're entering control-tower from outside
      if ((!this.currentRoute || !this.isControlTowerRoute(this.currentRoute)) && this.isControlTowerRoute(newRoute)) {
        // We're entering control-tower from a non-control-tower route (or initial load)
        this.previousRoute = this.currentRoute || '/dashboard'; // fallback to dashboard if no previous route
        // this.cachePreviousRoute(this.previousRoute);
      }

      this.currentRoute = newRoute;
      // this.cacheCurrentRoute(this.currentRoute);
    });
  }

  /**
   * Restore cached routes from sessionStorage on service initialization
   */
  private restoreCachedRoutes(): void {
    try {
      const cachedPreviousRoute = sessionStorage.getItem(NavigationHistoryService.CACHE_KEYS.PREVIOUS_ROUTE);
      const cachedCurrentRoute = sessionStorage.getItem(NavigationHistoryService.CACHE_KEYS.CURRENT_ROUTE);

      if (cachedPreviousRoute) {
        this.previousRoute = cachedPreviousRoute;
      }

      if (cachedCurrentRoute) {
        this.currentRoute = cachedCurrentRoute;
      }
    } catch (error) {
      console.warn('Failed to restore navigation history from cache:', error);
    }
  }

  /**
   * Cache the previous route to sessionStorage
   */
  private cachePreviousRoute(route: string | null): void {
    try {
      if (route) {
        sessionStorage.setItem(NavigationHistoryService.CACHE_KEYS.PREVIOUS_ROUTE, route);
      } else {
        sessionStorage.removeItem(NavigationHistoryService.CACHE_KEYS.PREVIOUS_ROUTE);
      }
    } catch (error) {
      console.warn('Failed to cache previous route:', error);
    }
  }

  /**
   * Cache the current route to sessionStorage
   */
  private cacheCurrentRoute(route: string | null): void {
    try {
      if (route) {
        sessionStorage.setItem(NavigationHistoryService.CACHE_KEYS.CURRENT_ROUTE, route);
      } else {
        sessionStorage.removeItem(NavigationHistoryService.CACHE_KEYS.CURRENT_ROUTE);
      }
    } catch (error) {
      console.warn('Failed to cache current route:', error);
    }
  }

  private isControlTowerRoute(route: string): boolean {
    return route.includes('/control-tower');
  }

  /**
   * Get the route that was active before entering control-tower
   */
  getPreviousRouteBeforeControlTower(): string | null {
    return this.previousRoute;
  }

  /**
   * Navigate back to the previous route before control-tower
   */
  navigateBackFromControlTower(contractId?: string, jobRefId?: string): void {
    if (this.previousRoute && this.previousRoute == '/dashboard') {
      this.router.navigate([`/wizard/${jobRefId}/job-contract/view`, contractId]);
    } else if (contractId && this.returnsToContract()) {
      this.router.navigate(['/contract-management/contracts', contractId, 'view-contract']);
    } else if (this.previousRoute) {
      this.router.navigateByUrl(this.previousRoute);
    }
  }

  /**
   * Whether back should land on the contract rather than replay the entry route
   */
  returnsToContract(): boolean {
    return NavigationHistoryService.CONTRACT_RETURN_ENTRY_ROUTES.some(route => this.previousRoute?.startsWith(route));
  }

  /**
   * Clear cached navigation history (useful for logout or session cleanup)
   */
  clearCache(): void {
    try {
      sessionStorage.removeItem(NavigationHistoryService.CACHE_KEYS.PREVIOUS_ROUTE);
      sessionStorage.removeItem(NavigationHistoryService.CACHE_KEYS.CURRENT_ROUTE);
      this.previousRoute = null;
      this.currentRoute = null;
    } catch (error) {
      console.warn('Failed to clear navigation history cache:', error);
    }
  }

  isDailyStatusReportRoute(route: string): boolean {
    return route.includes('/daily-status-report');
  }

  /**
   * Check if there's a previous route available
   */
  hasPreviousRoute(): boolean {
    return this.previousRoute !== null;
  }
}
